import React, { useState, useEffect } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import ScamDetectorAPI from "../../services/ScamDetectorAPI";
import { useTheme } from "../../context/ThemeContext";
import { useLoading } from "../../../../contexts/LoadingContext";
import ScamReportAPI from "../../services/ScamReportAPI";
import EmailManualScanMobile from "./EmailManualScanMobile";
import EmailManualScanDesktop from "./EmailManualScanDesktop";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";

const EmailManualScan = ({ setHeroContent }) => {
  const { effectiveTheme } = useTheme();
  const { t } = useTranslation();
  const { isLoading, showLoading, hideLoading } = useLoading();

  // Mobile detection
  const isMobile = () => window.innerWidth <= 768;
  const [showMobileView, setShowMobileView] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => setShowMobileView(isMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [emailContent, setEmailContent] = useState({
    subject: "",
    senderEmail: "",
    body: "",
  });
  const [userContext, setUserContext] = useState("");
  const [analysisResult, setAnalysisResult] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [error, setError] = useState(null);
  const [showConfidenceTooltip, setShowConfidenceTooltip] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailContent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoading(t("manual.loading.title"), t("manual.loading.subtitle"));
    setError(null);
    setAnalysisResult(null);

    try {
      const contentWithContext = { ...emailContent };
      if (userContext.trim()) {
        contentWithContext.userContext = userContext.trim();
      }
      const result = await ScamDetectorAPI.analyzeEmail(contentWithContext);
      setAnalysisResult(result);
      console.log("Analysis Result:", result);

      // Save report on detection
      if (result.isScam === true) {
        try {
          const saveResponse = await ScamReportAPI.saveReport({
            analysisType: "EMAIL_ANALYSIS",
            analysisTitle: emailContent.subject || "Email Scam Detected",
            analysisDetail:
              "This email contains suspicious or fraudulent content",
            analysisInput: emailContent.body || "",
            context: userContext?.trim() || undefined,
            isScam: true,
            confidence: result.confidence || 0,
            riskLevel: result.riskLevel || "high",
            explanation: result.explanation || "",
            suggestions: result.suggestions || "",
          });
          if (saveResponse.success) {
            setReportId(saveResponse.reportId);
          }
        } catch (saveErr) {
          console.error("[EmailManualScan] Failed to save report:", saveErr);
        }
      }
    } catch (err) {
      console.error("Error analyzing email:", err);
      setError(err.message || t("manual.error.generic"));
    } finally {
      hideLoading();
    }
  };

  const handleReset = () => {
    setEmailContent({
      subject: "",
      senderEmail: "",
      body: "",
    });
    setUserContext("");
    setAnalysisResult(null);
    setError(null);
  };

  const sharedProps = {
    emailContent,
    userContext,
    setUserContext,
    analysisResult,
    error,
    isLoading,
    reportId,
    handleInputChange,
    handleSubmit,
    handleReset,
  };

  return (
    <div
      className={`common-container${showMobileView ? " emsm-mobile-active" : ""}`}
      data-theme={effectiveTheme}
    >
      <ReadOnlyBanner
        onReturn={() => setHeroContent && setHeroContent("Member User Info")}
      />
      {showMobileView ? (
        <EmailManualScanMobile
          {...sharedProps}
          showConfidenceTooltip={showConfidenceTooltip}
          setShowConfidenceTooltip={setShowConfidenceTooltip}
        />
      ) : (
        <EmailManualScanDesktop {...sharedProps} />
      )}
    </div>
  );
};

export default EmailManualScan;
